import { z } from 'zod';
import { createModel, QueryMany, QueryOne } from '../../common';
import { businessSchema, productSchema } from '../../common/app-registry.schema';
import { dateSchema, objectIdSchema } from '../../validation';
import { referralCodeSchema } from './referral.code.schema';

/**
 * What kind of interaction a hit records.
 *
 * - `view`  — a page carrying the code was loaded.
 * - `click` — a tracked CTA was pressed (WhatsApp button, "Sign up",
 *             "Download app"). `target` says which.
 */
export const referralTrackingTypeSchema = z.enum(['view', 'click']);
export type ReferralTrackingType = z.infer<typeof referralTrackingTypeSchema>;

/**
 * Client-supplied context on a hit. Every field optional; the embed
 * script fills what it can, an external server-to-server caller may
 * send only `type`.
 */
export const referralTrackingContextSchema = z.object({
  /** `location.href` (query stripped of PII by the client). */
  url: z.string().trim().max(2048).optional(),
  /** `location.hostname` — `wathiqaplus.com`, `tryhero.io`, … */
  host: z.string().trim().max(255).optional(),
  /** `document.referrer`. */
  referrer: z.string().trim().max(2048).optional(),
  /**
   * For clicks: which CTA — `whatsapp`, `signup`, `app_store`,
   * `play_store`, or any `data-hero-target` value the host page set.
   */
  target: z.string().trim().max(64).optional(),
  /** Which brand's site / line the hit belongs to, when the page knows. */
  business: businessSchema.optional(),
  /** Which product the page was selling, when it knows. */
  product: productSchema.optional(),
  /** `navigator.language`. */
  language: z.string().trim().max(16).optional(),
  /**
   * The visitor's **first-touch** code when it differs from `code`.
   * A browser that arrived on partner A's link months ago and now opens
   * partner B's campaign link posts the hit for B (the ongoing /
   * campaign touch) with `originalCode = A`. Attribution and rewards
   * stay first-touch; this field is what lets the admin see the
   * overlap. Omitted when equal to `code` or unknown.
   */
  originalCode: z.string().trim().max(16).optional(),
  /**
   * PostHog `distinct_id` of the browser that produced the hit, when
   * the host page runs PostHog. The backend aliases it to the person it
   * later identifies (WhatsApp phone / `User._id`) so the anonymous web
   * session and the signed-up person are one PostHog Person.
   */
  posthogDistinctId: z.string().trim().max(200).optional(),
  /** UTM params present on the page, if any. */
  utm: z
    .object({
      source: z.string().max(128).optional(),
      medium: z.string().max(128).optional(),
      campaign: z.string().max(128).optional(),
      term: z.string().max(128).optional(),
      content: z.string().max(128).optional(),
    })
    .optional(),
});
export type ReferralTrackingContext = z.infer<typeof referralTrackingContextSchema>;

/**
 * `ReferralTracking` collection — one row per deduped view / click on a
 * referral link.
 *
 * Written by the public `POST /referral/tracking` endpoint (the embed
 * script on external sites, the landing site and the dashboard all post
 * here) and read by:
 *
 * - the admin panel, for per-link hit lists and funnels;
 * - `ReferralTrackingService.matchClosest`, which finds the latest hit
 *   inside a link's attribution window when a lead arrives with no code
 *   (someone clicked the partner's WhatsApp button and then messaged us).
 *
 * Dedupe is server-side: the same `visitorId` + `linkId` + `type` +
 * `context.target` within `CONFIG.referral.dedupeWindowMinutes` updates
 * `lastSeenAt` and `hits` on the existing row instead of inserting.
 * Rows carry no PII beyond an IP hash; the collection has a TTL index
 * on `doc.createdAt` (180 days) since anything older than a reward
 * cycle is analytics, and the link's `stats` already hold the counts.
 *
 * Expected indexes (created by the `referral-collections` migration):
 * - `{ linkId: 1, 'doc.createdAt': -1 }` — per-link list + closest match.
 * - `{ visitorId: 1, linkId: 1, type: 1, 'context.target': 1, lastSeenAt: -1 }` — dedupe.
 * - `{ 'doc.createdAt': 1 }` TTL 180d.
 * - `{ 'matched.leadId': 1 }` sparse — "which hit did this lead match".
 */
export const ReferralTrackingSchema = createModel({
  /** `ReferralLink._id` the code resolved to. */
  linkId: objectIdSchema,
  /** `ReferralPartner._id`, denormalised for partner-level reports. */
  partnerId: objectIdSchema,
  /** The code as received (normalised). */
  code: referralCodeSchema,
  /** `view` or `click`. */
  type: referralTrackingTypeSchema,
  /**
   * First-party visitor id from the `hero_vid` cookie (random, set by
   * the embed script / dashboard). Absent for server-to-server callers;
   * then dedupe falls back to the IP hash.
   */
  visitorId: z.string().trim().max(64).optional(),
  /** SHA-256 of the caller IP + a server salt. Never the raw IP. */
  ipHash: z.string().trim().max(64).optional(),
  /** Truncated `User-Agent`, for bot filtering in reports. */
  userAgent: z.string().trim().max(256).optional(),
  /** Page context supplied by the client. */
  context: referralTrackingContextSchema.default({}),
  /** How many times the deduped event repeated inside the window. */
  hits: z.coerce.number().int().min(1).default(1),
  /** Last time the deduped event repeated. Equals `doc.createdAt` on insert. */
  lastSeenAt: dateSchema,
  /**
   * Set when a lead was attributed to this hit through
   * `closest_tracking`, so the hit is not matched twice and the CRM can
   * show the join.
   */
  matched: z
    .object({
      leadId: objectIdSchema,
      at: dateSchema,
    })
    .optional(),
});

export type ReferralTracking = z.infer<typeof ReferralTrackingSchema.model>;
export type ReferralTrackingData = z.infer<typeof ReferralTrackingSchema.data>;
export type ReferralTrackingPartial = z.infer<typeof ReferralTrackingSchema.partial>;
export type ReferralTrackingQueryMany = QueryMany<ReferralTracking>;
export type ReferralTrackingQueryOne = QueryOne<ReferralTracking>;
